<template>
    <div class="lesson-goals">

        <div class="core-lesson-goal" v-if="showLessonCoreGoal">
            <h3 class="title">
                {{ $t('lesson_module.core_lesson_goal') }}
                <b-button
                    class="btn-print-lesson appear-disabled"
                    v-b-tooltip="$t('common.coming_soon')"
                    variant="outline-primary">
                    {{ $t('lesson.print_this_lesson') }}
                    <i class="fad fa-fw fad fa-print"></i>
                </b-button>
            </h3>

            <div class="core-lesson-goal-wrapper" v-if="editable && isAddingCoreGoal">
                <small v-if="coreLessonIsToLong" class="core-lesson-error">
                    {{ $t('lesson_module.core_lesson_goal_width_error') }}
                </small>
                <b-form-textarea
                    autofocus
                    rows="3"
                    cols="3"
                    id="core-lesson-goal-input"
                    v-model="coreLessonGoal"
                    :placeholder="$t('lesson_module.core_lesson_goal_placeholder')">
                </b-form-textarea>

                <b-button
                    class="btn-cancel"
                    variant="outline-primary"
                    @click="cancelCoreLessonGoal">
                    {{ $t('common.cancel') }}
                </b-button>

                <b-button
                    class="btn-save btn-store-core-goal"
                    v-b-tooltip.hover="$t('common.save')"
                    variant="primary"
                    @click="updateCoreLessonGoal">
                    {{ $t('common.save') }}
                </b-button>
            </div>

            <div class="core-lesson-goal-wrapper" v-else>
                <p class="description">{{ lessonIn.core_lesson_goal }}</p>

                <b-button
                    class="btn-edit btn-update-core-goal" v-if="!isAddingCoreGoal && editable"
                    variant="primary"
                    @click="enableCoreLessonGoalUpdate">
                    {{ $t('lesson_module.core_lesson_goal_edit_button_text') }}
                </b-button>
            </div>

        </div>

        <div class="goals-list">
            <h3 class="title">
                {{ $t('common.learning_goals') }}
                <b-button
                    v-if="!showLessonCoreGoal"
                    class="btn-print-lesson appear-disabled"
                    v-b-tooltip="$t('common.coming_soon')"
                    variant="outline-primary">
                    {{ $t('lesson.print_this_lesson') }}
                    <i class="fad fa-fw fad fa-print"></i>
                </b-button>
            </h3>
            <draggable
                v-if="lessonIn.goals.length > 0"
                group="goals"
                ghost-class="drag-ghost"
                :list="lessonIn.goals"
                handle=".goal-handle"
                @change="goalOrderChanged">
                <b-list-group-item
                    class="goal-item"
                    v-for="(goal, goalKey) in lessonIn.goals"
                    :class="editable && 'edit-modus'"
                    :key="goalKey"
                    active-class="active">
                    <div class="goal-title-wrapper">
                        <b-button
                            class="btn-drag-icon"
                            v-if="editable"
                            variant="none"
                            @click="$event.stopPropagation()">
                            <i class="fas fa-fw fa-arrows-alt-v goal-handle"></i>
                        </b-button>

                        <span class="goal-title">{{ goal.title }}</span>
                    </div>

                    <div
                        class="goal-actions"
                        v-if="editLessonGoal?.id !== goal.id && editable">
                        <b-button
                            class="btn-edit"
                            v-b-tooltip="$t('common.edit_learning_goal')"
                            variant="primary-icon-only"
                            @click="editGoal(goal)">
                        </b-button>
                        <b-button
                            class="btn-remove"
                            v-b-tooltip="$t('common.delete_learning_goal')"
                            variant="primary-icon-only"
                            @click="removeGoal(goal)">
                        </b-button>
                    </div>

                    <div
                        class="inline-goal-title-edit"
                        v-if="inlineEditIsInProgress && editable && editLessonGoal?.id === goal.id">
                        <b-form-input v-model="editLessonGoal.title"></b-form-input>
                        <b-button
                            class="btn-checked btn-update"
                            v-b-tooltip.hover="$t('common.save')"
                            variant="success-icon-only"
                            @click="updateLessonGoal">
                        </b-button>
                        <b-button
                            class="btn-times btn-cancel"
                            v-b-tooltip.hover="$t('common.cancel')"
                            variant="primary-icon-only"
                            @click="cancelGoalTitleUpdate">
                        </b-button>
                    </div>
                </b-list-group-item>
            </draggable>

            <div
                class="not-found with-header empty-lesson-goal-list"
                v-if="lessonIn.goals.length === 0 && !newGoalCreationIsInProgress">
                <div
                    class="btn-add-circle icon"
                    v-if="editable"
                    v-b-tooltip.hover
                    :title="$t('common.add_learning_goal')"
                    @click="createGoal"></div>
                <div class="alert-notice">{{ $t('common.no_learning_goals_found') }}</div>
            </div>

            <div
                class="goal-management"
                v-if="editable">
                <b-button
                    class="btn-add"
                    v-if="lessonIn.goals.length > 0 && !newGoalCreationIsInProgress"
                    variant="primary"
                    @click="createGoal">
                    {{ $t('common.add_learning_goal') }}
                </b-button>

                <b-form-group
                    class="new-goal-group"
                    v-if="newGoalCreationIsInProgress">
                    <b-form-input
                        class="add-lesson-goal"
                        v-model="newLessonGoal"
                        autofocus
                        :placeholder="$t('lesson.add_learning_goal')">
                    </b-form-input>

                    <b-button
                        class="btn-cancel"
                        variant="outline-primary"
                        @click="cancelStoreLessonGoal">
                        {{ $t('common.cancel') }}
                    </b-button>

                    <b-button
                        class="btn-save"
                        variant="primary"
                        @click="storeLessonGoal">
                        {{ $t('common.save') }}
                    </b-button>
                </b-form-group>
            </div>
        </div>

    </div>
</template>

<script>
    import draggable from 'vuedraggable';

    export default {
        name: 'design-2021-lesson-manual-lesson-goal-component',
        components: {
            draggable
        },
        props: {
            lessonIn: {
                type: Object,
                required: true
            },
            editable: {
                type: Boolean,
                required: false,
                default: false
            }
        },
        data() {
            return {
                newGoalCreationIsInProgress: false,
                newLessonGoal: '',
                editLessonGoal: null,
                inlineEditIsInProgress: false,
                isAddingNewGoal: false,
                isAddingCoreGoal: this.lessonIn.core_lesson_goal === '',
                initialTitle: '',
                coreLessonGoal: this.lessonIn.core_lesson_goal,
                coreLessonIsToLong: false
            };
        },
        computed: {
            showLessonCoreGoal() {
                return this.lessonIn.core_lesson_goal?.length || this.editable;
            }
        },
        methods: {
            createGoal() {
                this.newGoalCreationIsInProgress = true;
            },
            editGoal(goal) {
                this.initialTitle = goal.title;
                this.editLessonGoal = goal;
                this.inlineEditIsInProgress = true;
            },
            storeLessonGoal() {
                let data = {
                    title: this.newLessonGoal
                };

                this.axios.post(this.routes.storeLessonGoal(this.lessonIn.id), data).then(
                    (response) => {
                        this.$emit('lesson-updated', response.data);
                        this.newGoalCreationIsInProgress = false;
                        this.newLessonGoal = '';
                    },
                    (error) => {
                        this.alert.ajaxError(error);
                    }
                );
            },
            cancelStoreLessonGoal() {
                this.newGoalCreationIsInProgress = false;
                this.newLessonGoal = '';
            },

            updateLessonGoal() {
                let data = {
                    title: this.editLessonGoal.title
                };

                this.axios.post(this.routes.updateLessonGoal(this.lessonIn.id, this.editLessonGoal.id), data).then(
                    (response) => {
                        this.$emit('lesson-updated', response.data);
                        this.editLessonGoal = null;
                        this.inlineEditIsInProgress = false;
                    },
                    (error) => {
                        this.alert.ajaxError(error);
                    }
                );
            },
            updateCoreLessonGoal() {
                this.validateCoreLessonGoal();


                if (this.coreLessonIsToLong) {
                    return;
                }

                let data = {
                    core_lesson_goal: this.coreLessonGoal
                };

                this.axios.post(this.routes.updateCoreLessonGoal(this.lessonIn.id), data).then(
                    (response) => {
                        this.isAddingCoreGoal = false;
                        this.$emit('lesson-updated', response.data);
                    },
                    (error) => {
                        this.alert.ajaxError(error);
                    }
                );
            },

            removeGoal(goal) {
                this.alert
                    .confirm(
                        this.$t('common.delete_learning_goal'),
                        this.$t('common.are_you_sure_you_want_to_delete_this_item')
                    )
                    .then((confirm) => {
                        if (confirm) {
                            this.alert.saving();

                            this.axios.delete(this.routes.deleteLessonGoal(this.lessonIn.id, goal.id)).then(
                                (response) => {
                                    this.$emit('lesson-updated', response.data);
                                },
                                (error) => {
                                    return this.alert.ajaxError(error);
                                }
                            );
                        }
                    });
            },
            goalOrderChanged() {
                let array = [];
                let count = 0;

                for (const goal of this.lessonIn.goals) {
                    count++;
                    array.push({
                        id: goal.id,
                        order: count
                    });
                }

                let data = {
                    goals: array
                };

                this.axios.post(this.routes.updateGoalOrder(this.lessonIn.id), data).then(
                    (response) => {
                    }, //no action required
                    (error) => {
                        this.alert.ajaxError(error);
                    }
                );
            },

            cancelGoalTitleUpdate() {
                this.editLessonGoal.title = this.initialTitle;
                this.inlineEditIsInProgress = false;
                this.editLessonGoal = null;
            },

            cancelCoreLessonGoal() {
                this.isAddingCoreGoal = false;
                this.coreLessonGoal = this.lessonIn.core_lesson_goal;
            },

            enableCoreLessonGoalUpdate() {
                this.isAddingCoreGoal = true;
            },
            validateCoreLessonGoal() {
                this.coreLessonIsToLong = this.coreLessonGoal.length > 430;
            }
        }
    };
</script>

<style scoped lang="scss">
    .core-lesson-goal-wrapper {
        display: flex;
        flex-wrap: wrap;
        gap: map-get($grid-gutter-spacing, 'fifteen') $grid-gutter-width;
        align-items: center;
        justify-content: flex-start;
        margin-bottom: $grid-gutter-width;

        textarea {
            flex: 1 0 100%;
            resize: none;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            line-clamp: 3;
            -webkit-box-orient: vertical;
            height: 86px;
        }

        .btn-cancel {
            line-height: 1;
        }

        .description {
            margin: 0;
            flex: 1 0 100%;
        }
    }

    .core-lesson-error {
        flex: 1 0;
        border-radius: $border-radius-sm;
        padding: map-get($grid-gutter-spacing, 'four') map-get($grid-gutter-spacing, 'fifteen');
        background-color: $danger;
        color: $white;
        font-size: 0.8125rem;
        font-weight: $font-weight-medium;
    }


    .lesson-goals {
        padding: map-get($grid-gutter-spacing, 'fifteen');
        background-color: var(--intermediateColor, darken($primary, 20%));
        color: $white;

        .title {
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
    }

    .btn-drag-icon {
        padding: 0;
    }

    .goal-title {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 0;
    }

    .btn-print-lesson,
    .btn-cancel {
        border-color: $white;
        color: $white;

        &:hover,
        &:focus,
        &:active,
        &:focus,
        &:not(:disabled):not(.disabled):active:focus {
            background-color: $white;
            border-color: $white;
            color: var(--primary);
        }

        &.appear-disabled {
            opacity: 0.75;
        }
    }

    .goals-list {
        .goal-item {
            display: flex;
            align-items: center;
            flex-direction: row;
            justify-content: flex-start;
            column-gap: map-get($grid-gutter-spacing, 'eight');
            border: none;
            background-color: transparent;
            padding: 0 0 map-get($grid-gutter-spacing, 'fifteen') 0;

            &:not(.edit-modus) {
                &:before {
                    display: inline-block;
                    content: '\f00c';
                    font-family: 'Font Awesome 5 Pro';
                    font-weight: $font-weight-bold;
                    font-size: $default-font-size;
                    width: 1.125rem;
                    color: $white;
                    margin-right: map-get($grid-gutter-spacing, 'eight');
                }
            }

            &.edit-modus {
                flex-wrap: wrap;
                row-gap: map-get($grid-gutter-spacing, 'ten');
                border-bottom: 3px dotted $white;
                margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
            }
        }
    }

    .goal-title-wrapper,
    .goal-actions {
        display: flex;
        align-items: center;
    }

    .goal-title-wrapper {
        column-gap: map-get($grid-gutter-spacing, 'eight');

        &:has(+ .inline-goal-title-edit) {
            display: none;
        }
    }

    .goal-actions {
        margin-left: auto;
        column-gap: map-get($grid-gutter-spacing, 'fifteen');
        flex: 1 0 100%;
        align-self: flex-end;
        justify-content: flex-end;

        .btn {
            color: $white;

            &:before,
            &:after {
                margin: 0;
            }

            &:hover {
                color: $black;
            }
        }
    }

    .inline-goal-title-edit {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        column-gap: map-get($grid-gutter-spacing, 'eight');

        input {
            margin: 0;
        }

        .btn {
            line-height: 1.5;
            font-size: 1.125rem;

            &:before {
                margin-right: 0;
            }
        }

        .btn-update {
            &:hover,
            &:focus,
            &:not(:disabled):not(.disabled):active {
                background-color: transparent;
                color: $success;
            }
        }

        .btn-cancel {
            &:hover,
            &:focus,
            &:not(:disabled):not(.disabled):active {
                background-color: transparent;
                color: $white;
            }
        }
    }

    .goal-management {
        margin-top: map-get($grid-gutter-spacing, 'forty-two');
    }

    .empty-lesson-goal-list {
        .alert-notice {
            color: $white;
        }
    }

    .new-goal-group ::v-deep {
        > div {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: flex-start;
            gap: map-get($grid-gutter-spacing, 'fifteen');

            input {
                flex: 1 0 100%;
            }

            .btn {
                line-height: 1;
            }
        }
    }

    @media (min-width: map-get($grid-breakpoints, 'md')) {
        .goal-title-wrapper {
            flex: 1;

            .btn-drag-icon {
                align-self: flex-start;
            }
        }

        .goal-actions {
            flex: 0 0 auto;
            align-self: flex-start;
        }
    }
</style>
